/**
 * modeRegistry — canonical 5-mode SSoT for Cycle 101 Workspace-V2.
 *
 * Sprint-2 Task 2.2 (sprint.md:207). The 5 modes here (WORLD /
 * GEOPOLITICAL / FINANCE / MARITIME / INVESTIGATIONS) are the V2
 * mode vocabulary per master plan §Step 1 + SDD §5.1.
 *
 * Cycle-109 sprint-204 S2.6 (2026-05-21): accent regression corrected
 * to match canonical `output/design_reference/design_handoff_echelon_2026_04_26/
 * workspace/domainModes.jsx:19-127`. Prior values were shifted off-by-one
 * (each mode held the next canonical mode's accent). `description` field
 * added per canonical shape; `MODE_ORDER` exported as the explicit
 * canonical-ordering const. Q-S2.0-a precedent: extend modeRegistry.ts in
 * place rather than fork to a new `domainModes.ts` file.
 *
 * Relationship to `Mode` in `hooks/useWorkspaceState.ts`:
 *   - Legacy `Mode = 'world' | 'finance' | 'commodities' | 'tech' | 'investigations'`
 *     (the renamed-from-Lens type) is still used by V1 callers.
 *   - `WorkspaceModeId` here is the V2 vocabulary — `commodities` and
 *     `tech` retire; `geopolitical` and `maritime` arrive.
 *   - Sprint-3 introduces `useModeCanvasLayout(mode: WorkspaceModeId)`
 *     which becomes the primary dispatch path. Sprint-5 retires legacy
 *     values once all call sites migrate.
 *
 * Design tokens: accent per mode maps to a `--cd-sig-*` hue from
 * `frontend/src/styles/design-system.css` + `legacy-aliases.css`. Glyphs
 * match master plan §Step 1 canonical notation.
 */

export type WorkspaceModeId =
  | 'world'
  | 'geopolitical'
  | 'finance'
  | 'maritime'
  | 'investigations';

export type WorkspaceAccentVar =
  | '--cd-sig-neutral'
  | '--cd-sig-iris'
  | '--cd-sig-ember'
  | '--cd-sig-azure'
  | '--cd-sig-mint'
  | '--cd-sig-amber';

export interface WorkspaceModeDef {
  /** Stable identifier — used in URLs, localStorage keys, telemetry. */
  readonly id: WorkspaceModeId;
  /** Display label — uppercase per master plan §Step 1. */
  readonly label: string;
  /** Single-glyph shorthand — per master §Step 1 canonical notation. */
  readonly glyph: string;
  /** CSS custom-property name for the mode's accent hue. */
  readonly accentVar: WorkspaceAccentVar;
  /** Short description shown in ModeIdentityStrip (Task 2.3, session 2). */
  readonly tagline: string;
  /** Long-form mode description per canonical domainModes.jsx — surfaced in
   *  AddModulePicker tooltips and identity-strip expanded state. */
  readonly description: string;
  /** Keyboard shortcut key (without modifier) — ⌘1..⌘5. */
  readonly shortcut: string;
}

export const MODE_REGISTRY: readonly WorkspaceModeDef[] = [
  {
    id: 'world',
    label: 'WORLD',
    glyph: '◉',
    accentVar: '--cd-sig-neutral',
    tagline: 'Cross-domain intelligence feed',
    description:
      'Cross-domain signal synthesis. Forge proposals, active investigations, and one anchor module per domain.',
    shortcut: '1',
  },
  {
    id: 'geopolitical',
    label: 'GEOPOLITICAL',
    glyph: '▲',
    accentVar: '--cd-sig-iris',
    tagline: 'Conflict + state-actor pressure',
    description:
      'State-actor posture, conflict density, civil unrest, displacement, and declassified intelligence.',
    shortcut: '2',
  },
  {
    id: 'finance',
    label: 'FINANCE',
    glyph: '●',
    accentVar: '--cd-sig-ember',
    tagline: 'Markets + macro flows',
    description:
      'Market conditions, macro indicators, commodity flows, and supply-chain stress. Hormuz tracker pinned for oil exposure.',
    shortcut: '3',
  },
  {
    id: 'maritime',
    label: 'MARITIME',
    glyph: '◧',
    accentVar: '--cd-sig-azure',
    tagline: 'Vessel + chokepoint watch',
    description:
      'Vessel traffic, port events, choke-point density, and AIS gaps. Hormuz tracker pinned full-width.',
    shortcut: '4',
  },
  {
    id: 'investigations',
    label: 'INVESTIGATIONS',
    glyph: '◆',
    accentVar: '--cd-sig-mint',
    tagline: 'Operator research surface',
    description:
      'Investigation-centric layout. Brief, desk, evidence, claims, and informs — with the forge lane anchoring the top.',
    shortcut: '5',
  },
] as const;

/**
 * Canonical mode ordering per `domainModes.jsx:127`. Drives the ModesRow
 * chip-row and any callers that need deterministic iteration order.
 */
export const MODE_ORDER: readonly WorkspaceModeId[] = [
  'world',
  'geopolitical',
  'finance',
  'maritime',
  'investigations',
] as const;

export const MODE_IDS: readonly WorkspaceModeId[] = MODE_ORDER;

/** Look up a mode definition by id. Returns `undefined` for unknown ids. */
export function getModeDef(id: WorkspaceModeId): WorkspaceModeDef | undefined {
  return MODE_REGISTRY.find((m) => m.id === id);
}

/** Type guard — narrows `unknown` or `string` to `WorkspaceModeId`. */
export function isWorkspaceModeId(value: unknown): value is WorkspaceModeId {
  return typeof value === 'string' && MODE_IDS.includes(value as WorkspaceModeId);
}
